import React, { useEffect, useState } from "react";
import { BarChart3, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * EnrichCompletenessCard — admin tile on /admin that visualises how much of
 * the catalogue has structured-facts populated (dog_policy, family_friendly,
 * open_year_round, pitch_surface, campfires).
 *
 * Surfaces:
 *  - Per-score distribution (how many sites have 5/5, 4/5, 3/5, …, 0/5)
 *  - Per-field known/unknown counts with a progress bar
 *  - "Needed enrichment" count (matches the existing Enrich Facts tile)
 */
const FIELD_LABELS = {
  dog_policy: "Dog policy",
  family_friendly: "Family friendly",
  open_year_round: "Open year-round",
  pitch_surface: "Pitch surface",
  campfires: "Campfires",
};

const SCORE_COLORS = {
  5: { bg: "#1f7a35", fg: "#fff" },
  4: { bg: "#3fa552", fg: "#fff" },
  3: { bg: "#d4a017", fg: "#fff" },
  2: { bg: "#e07a14", fg: "#fff" },
  1: { bg: "#e63e00", fg: "#fff" },
  0: { bg: "#9a9a9a", fg: "#fff" },
};

export default function EnrichCompletenessCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/enrich-facts/completeness");
      setData(data);
    } catch (e) {
      toast.error("Could not load completeness stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const total = data?.total || 0;
  const fivePct = total ? Math.round(((data?.by_score?.["5"] || 0) / total) * 100) : 0;

  return (
    <section
      className="border hairline rounded-2xl p-5 sm:p-6 mb-6 bg-white"
      data-testid="admin-enrich-completeness-card"
    >
      <header className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="eyebrow text-[#0e7d3a] flex items-center gap-1.5 mb-1">
            <BarChart3 className="w-3 h-3" /> AI Data Completeness
          </div>
          <h2 className="font-display text-xl sm:text-2xl tracking-tight">
            How filled-in is the catalogue?
          </h2>
          <p className="text-xs text-[#666] mt-1 max-w-xl">
            Tracks 5 filter-critical facts per site:{" "}
            <code className="bg-[#f5f5f5] px-1 rounded">dog_policy</code>,{" "}
            <code className="bg-[#f5f5f5] px-1 rounded">family_friendly</code>,{" "}
            <code className="bg-[#f5f5f5] px-1 rounded">open_year_round</code>,{" "}
            <code className="bg-[#f5f5f5] px-1 rounded">pitch_surface</code>,{" "}
            <code className="bg-[#f5f5f5] px-1 rounded">campfires</code>.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-xs text-[#666] hover:text-[#0e7d3a] inline-flex items-center gap-1 disabled:opacity-50"
          data-testid="enrich-completeness-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      {!data && (
        <div className="text-sm text-[#666]">Loading stats…</div>
      )}

      {data && (
        <>
          {/* Headline summary */}
          <div className="grid sm:grid-cols-3 gap-3 mb-5">
            <SummaryStat
              label="Total campsites"
              value={total.toLocaleString()}
              testid="completeness-total"
            />
            <SummaryStat
              label="Fully populated (5/5)"
              value={`${(data.by_score?.["5"] || 0).toLocaleString()} (${fivePct}%)`}
              accent="#1f7a35"
              testid="completeness-fivefive"
            />
            <SummaryStat
              label="Still needing enrichment"
              value={(data.needed_count || 0).toLocaleString()}
              accent={data.needed_count > 0 ? "#e63e00" : "#1f7a35"}
              testid="completeness-needed"
            />
          </div>

          {/* Per-score distribution bar */}
          <div className="mb-5">
            <div className="mono text-[10px] uppercase tracking-wider text-[#666] mb-2">
              Distribution by fact-count
            </div>
            <div className="flex h-7 rounded-lg overflow-hidden border hairline" data-testid="completeness-distribution">
              {[5, 4, 3, 2, 1, 0].map((score) => {
                const count = data.by_score?.[String(score)] || 0;
                const pct = total ? (count / total) * 100 : 0;
                if (pct === 0) return null;
                const { bg, fg } = SCORE_COLORS[score];
                return (
                  <div
                    key={score}
                    className="flex items-center justify-center text-[10px] font-bold mono"
                    style={{ width: `${pct}%`, background: bg, color: fg, minWidth: pct > 2 ? "auto" : 0 }}
                    title={`${score}/5 facts: ${count.toLocaleString()} sites (${pct.toFixed(1)}%)`}
                  >
                    {pct > 4 ? `${score}/5` : ""}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-[#666]">
              {[5, 4, 3, 2, 1, 0].map((score) => {
                const count = data.by_score?.[String(score)] || 0;
                if (count === 0) return null;
                return (
                  <span key={score} className="inline-flex items-center gap-1">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-sm"
                      style={{ background: SCORE_COLORS[score].bg }}
                    />
                    {score}/5: <strong>{count.toLocaleString()}</strong>
                  </span>
                );
              })}
            </div>
          </div>

          {/* Per-field breakdown */}
          <div>
            <div className="mono text-[10px] uppercase tracking-wider text-[#666] mb-2">
              Coverage by field
            </div>
            <div className="space-y-2" data-testid="completeness-fields">
              {(data.tracked_fields || []).map((field) => {
                const stats = data.per_field?.[field] || { known: 0, unknown: 0 };
                const knownPct = total ? (stats.known / total) * 100 : 0;
                const isFull = stats.unknown === 0;
                return (
                  <div key={field} className="flex items-center gap-3" data-testid={`field-${field}`}>
                    <div className="w-32 text-xs text-[#0e0e0e] flex items-center gap-1.5">
                      {isFull ? (
                        <CheckCircle2 className="w-3 h-3 text-[#1f7a35] flex-shrink-0" />
                      ) : (
                        <AlertCircle className="w-3 h-3 text-amber-600 flex-shrink-0" />
                      )}
                      {FIELD_LABELS[field] || field}
                    </div>
                    <div className="flex-1 h-5 bg-[#f0f0f0] rounded overflow-hidden relative">
                      <div
                        className="h-full transition-all"
                        style={{
                          width: `${knownPct}%`,
                          background: isFull
                            ? "#1f7a35"
                            : knownPct > 90
                              ? "#3fa552"
                              : knownPct > 70
                                ? "#d4a017"
                                : "#e07a14",
                        }}
                      />
                    </div>
                    <div className="w-32 text-right text-xs mono text-[#0e0e0e]">
                      <strong>{stats.known.toLocaleString()}</strong>
                      <span className="text-[#999]"> / {total.toLocaleString()}</span>
                      <span className="text-[#666] ml-1">({knownPct.toFixed(1)}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {data.needed_count > 0 && (
            <div className="mt-4 p-3 bg-[#fff8ed] border border-[#f5d99e] rounded-lg text-xs text-[#7a4f00]">
              <strong>{data.needed_count.toLocaleString()} sites</strong> still need Claude enrichment.
              Use the “AI catalogue enrichment” tile below to run a batch.
            </div>
          )}
        </>
      )}
    </section>
  );
}

function SummaryStat({ label, value, accent, testid }) {
  return (
    <div className="bg-[#fafafa] border hairline rounded-xl p-3" data-testid={testid}>
      <div className="mono text-[10px] uppercase tracking-wider text-[#666] mb-1">{label}</div>
      <div
        className="font-display text-xl font-bold"
        style={{ color: accent || "#0e0e0e" }}
      >
        {value}
      </div>
    </div>
  );
}
